// Fog and lens dimming over the sea: a plane just above the ocean, drawn after it.
//
//   fog ..... parchment with ink ripples along the bathymetry contours (three lines off each coast),
//             where the fog field reaches the sea (fogField.ts bleeds stale coasts into it)
//   lens .... a slate veil that mutes the sea while a lens colours the land
//   strategic .. flat pastel sea, shallows lighter than the deep, sea ice white; the 3D ocean is hidden
//
// The plane exists only while there is something to draw (fog over the sea, a lens, the strategic
// view or a fold in progress), so a clear map pays nothing for it. Land is above the plane and hides it.

import * as THREE from 'three/webgpu';
import { Fn, If, float, fwidth, max, mix, positionWorld, smoothstep, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { ROWS, ROW_H, WORLD_W } from '../../core/grid.ts';
import { SEA_Y } from '../terrain/heights.ts';
import { rgb, screen } from '../tsl/color.ts';
import { hexFrag, wrapXNode } from '../tsl/hex.ts';
import { noiseW } from './wrapNoise.ts';
import type { HexData } from '../hexdata.ts';
import type { FogState } from './fogState.ts';
import { BURN_EMBER, BURN_INK, FOG_ACTIVE, INK, fieldAt, fogLook, paperAt } from './parchment.ts';
import { PASTEL } from './strategicPalette.ts';

type F = Node<'float'>;

/** Sits this far above the ocean plane; land (>= 0.056) stays above it. */
const VEIL_LIFT = 0.006;
const PLANE_MARGIN = 60;
const RENDER_ORDER = 2;
const POLAR_FADE = 30;
const VISIBLE_EPS = 0.001;

/** Sea keeps more of its colour than land in full fog (the paper is thin over water). */
const SEA_MAX = 0.8;
const LENS_FOG_CUT = 0.85;
const LENS_DIM = 0.3;
const LENS_TONE = '#2b3340';
const PAPER_AGE = 0.35;

// Strategic sea: pastel by depth, sea ice toward white.
const SEA_SHALLOW_M = 30;
const SEA_DEEP_M = 900;

// Ripples: lines at equal steps of sqrt(depth in metres), the first three off the coast.
const DEPTH_WARP = 0.45;
const DEPTH_WARP_FREQ = 0.7;
const RIPPLE_PER_ROOT = 0.22;
const RIPPLE_LINES = 3;
const RIPPLE_PX = 0.8;
const RIPPLE_INK = 0.5;
const RIPPLE_SPACING_PX = [3, 9] as const;
const EPS = 1e-6;

export interface SeaVeil {
  mesh: THREE.Mesh;
  /** Show it once so its pipeline compiles with the rest (an invisible mesh is skipped). */
  warm(): void;
  update(): void;
  dispose(): void;
}

/** How far the strategic view has faded in, as a shader node and as a number. */
export interface StrategicAmount {
  node: Node<'float'>;
  current(): number;
}

export function createSeaVeil(data: HexData, state: FogState, lensAmount: Node<'float'>, amountNow: () => number, strategic: StrategicAmount): SeaVeil {
  const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  material.polygonOffset = true;
  material.polygonOffsetFactor = -2;
  material.polygonOffsetUnits = -2;

  const alphaOut = float(0).toVar('veilAlpha');

  material.colorNode = Fn(() => {
    const world = positionWorld;
    const xz = vec2(wrapXNode(world.x), world.z);
    const hex = hexFrag(world.xz);
    const field = fieldAt(state.data, hex).toVar('veilField');
    const px = fwidth(world.xz).length().toVar('veilPx');
    const sheet = paperAt(xz, px, float(PAPER_AGE)).toVar('veilPaper');

    // Depth contours as ripples; the derivative stays outside the branch.
    const warp = vec2(noiseW(xz, DEPTH_WARP_FREQ), noiseW(xz.add(vec2(0, 23.9)), DEPTH_WARP_FREQ)).sub(0.5).mul(DEPTH_WARP * 2);
    const relief = data.reliefSmooth(hexFrag(world.xz.add(warp), 'veilDepthHex')).toVar('veilRelief');
    const depthM = max(relief.x.negate(), 0);
    const phase = depthM.sqrt().mul(RIPPLE_PER_ROOT).toVar('veilPhase');
    const dPhase = fwidth(phase).toVar('veilDPhase');

    const painted = vec3(0).toVar('veilPainted');
    const fogA = float(0).toVar('veilFog');
    const ember = vec3(0).toVar('veilEmber');
    const lens = lensAmount;
    const active = field.x.max(field.y).greaterThan(FOG_ACTIVE);

    If(active, () => {
      const look = fogLook(field, xz, state.reveal);
      const nearest = phase.add(0.5).floor();
      const dist = phase.sub(nearest).abs().div(dPhase.max(EPS));
      const inRange = smoothstep(RIPPLE_LINES + 0.5, RIPPLE_LINES - 0.5, nearest).mul(smoothstep(0.4, 0.9, nearest));
      const spacing = float(1).div(dPhase.max(EPS));
      const ripple = smoothstep(RIPPLE_PX + 1, RIPPLE_PX, dist).mul(inRange).mul(smoothstep(RIPPLE_SPACING_PX[0], RIPPLE_SPACING_PX[1], spacing)).mul(RIPPLE_INK);
      const inked = mix(sheet, rgb(INK), ripple);
      painted.assign(mix(inked, rgb(BURN_INK), look.rim.mul(0.85)));
      fogA.assign(look.cover.mul(SEA_MAX));
      ember.assign(rgb(BURN_EMBER).mul(look.rim).mul(0.6));
    });

    // Under a lens the fog steps back and a slate veil mutes the sea.
    const fog = fogA.mul(float(1).sub(lens.mul(LENS_FOG_CUT)));
    const dim = lens.mul(LENS_DIM);
    const polar = max(smoothstep(0, POLAR_FADE, world.z.negate()), smoothstep(ROWS * ROW_H, ROWS * ROW_H + POLAR_FADE, world.z));
    const veilAlpha = max(fog, dim).mul(float(1).sub(polar));
    const veilColour = mix(painted.add(ember), screen(LENS_TONE), lens.mul(float(1).sub(fog)));

    // Strategic: an opaque pastel sea that replaces the veil as the view fades in.
    const shelf = smoothstep(SEA_SHALLOW_M, SEA_DEEP_M, depthM);
    const water = mix(mix(rgb(PASTEL.seaShallow), rgb(PASTEL.seaDeep), shelf), rgb(PASTEL.seaIce), relief.z.clamp(0, 1));
    alphaOut.assign(max(veilAlpha, strategic.node).clamp(0, 1));
    return mix(veilColour, water, strategic.node);
  })();
  material.opacityNode = alphaOut;

  const geo = new THREE.PlaneGeometry(WORLD_W * 3, ROWS * ROW_H + PLANE_MARGIN * 2, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(WORLD_W / 2, SEA_Y + VEIL_LIFT, (ROWS * ROW_H) / 2);
  mesh.name = 'seaVeil';
  mesh.renderOrder = RENDER_ORDER;
  mesh.visible = false;

  return {
    mesh,
    warm() {
      mesh.visible = true;
    },
    update() {
      mesh.visible = state.hasSea() || state.animating() || amountNow() > VISIBLE_EPS || strategic.current() > VISIBLE_EPS;
    },
    dispose() {
      mesh.removeFromParent();
      geo.dispose();
      material.dispose();
    },
  };
}
